import React, { useState, useEffect } from 'react';
import { ProviderUser } from '../../domain/types/ProviderUser';
import { useProviderUsers } from '../../domain/services/hooks/useProviderUsers';
import { useUser } from '../../contexts/UserContext';
import * as Icon from 'react-feather';
import { Toast } from 'flowbite-react';
import { signIn } from '../../domain/services/auth/authService';
import { FirestoreAppUserRepository } from '../../infrastructure/repositories/appUserRepository';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import { GoogleCalendarService } from '../../domain/services/googleCalendarService';
import { GoogleEmailService } from '../../domain/services/googleEmailService';
import { HiCheck, HiX } from 'react-icons/hi';

export default function UserAccounts() {
  const googleCalendarService = GoogleCalendarService.getInstance();
  const googleEmailService = GoogleEmailService.getInstance();

  const { appUser } = useUser();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { providerUsers, loading, error } = useProviderUsers();
  const [componentError, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // This will re-run when refreshKey changes
  }, [refreshKey]);

  const refreshProviderUsers = () => {
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setShowErrorToast(true);
    setTimeout(() => setShowErrorToast(false), 3000);
  };

  const handleMakePrimary = async (providerUserId: string) => {
    try {
      if (appUser) {
        const appUserRepository = new FirestoreAppUserRepository();
        await appUserRepository.setPrimaryProviderUser(appUser.appUserId, providerUserId);
        refreshProviderUsers();
      }
    } catch (err) {
      console.error('Error making account primary:', err);
      setError(err as Error);
    }
  };

  const handleConnectCalendar = async (providerUser: ProviderUser) => {
    try {
      await googleCalendarService.connectCalendar(providerUser);
      refreshProviderUsers();
    } catch (err) {
      console.error('Error connecting calendar:', err);
      setError(err as Error);
    }
  };

  const handleDisconnectCalendar = async (providerUser: ProviderUser) => {
    try {
      await googleCalendarService.disconnectCalendar(providerUser);
      showSuccess('Google Calendar has been disconnected.');
      refreshProviderUsers();
    } catch (err) {
      console.error('Error disconnecting calendar:', err);
      setError(err as Error);
    }
  };

  const handleConnectGmail = async (providerUser: ProviderUser) => {
    try {
      await googleEmailService.connectGmail(
        providerUser,
        () => {
          showSuccess('Gmail has been successfully connected!');
          refreshProviderUsers();
        },
        (error: Error) => {
          showError(`Failed to connect Gmail: ${error.message}`);
        }
      );
    } catch (err) {
      console.error('Error connecting Gmail:', err);
      setError(err as Error);
    }
  };

  const handleDisconnectGmail = async (providerUser: ProviderUser) => {
    try {
      await googleEmailService.disconnectGmail(
        providerUser,
        () => {
          showSuccess('Gmail has been disconnected.');
          refreshProviderUsers();
        },
        (error: Error) => {
          showError(`Failed to disconnect Gmail: ${error.message}`);
        }
      );
    } catch (err) {
      console.error('Error disconnecting Gmail:', err);
      setError(err as Error);
    }
  };

  if (loading || !appUser) {
    return <LoadingState message="Loading user data..." />;
  }

  if (error || componentError) {
    return <ErrorState error={(error || componentError) as Error} />;
  }

  return (
    <section className="relative pb-16 lg:pb-24">
      <div className="container relative mx-auto px-4">
        <div className="rounded-md shadow dark:shadow-gray-700 bg-white dark:bg-slate-900">
          <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center p-6 border-b border-gray-100 dark:border-gray-700">
            <h5 className="text-xl font-semibold text-center md:text-left mb-4 md:mb-0">
              Connected Accounts
            </h5>

            <button
              className="flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 whitespace-nowrap transition-colors duration-200"
              onClick={() => signIn()}
            >
              <Icon.Plus className="mr-2 flex-shrink-0" />
              Connect More Accounts
            </button>
          </div>
          <div className="p-6">
            <div className="grid gap-6">
              {providerUsers.map((providerUserItem: ProviderUser) => (
                <div
                  key={providerUserItem.uid}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-6 bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex items-center w-full sm:w-auto">
                    <div className="relative">
                      <img
                        src={providerUserItem.photoURL || '/default-avatar.png'}
                        alt={providerUserItem.displayName || 'User Avatar'}
                        className="w-14 h-14 rounded-full ring-4 ring-gray-50 dark:ring-gray-700"
                      />
                      {providerUserItem.uid === appUser.primaryProviderUserId && (
                        <div className="absolute -top-2 -right-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
                          Primary
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <h6 className="text-lg font-semibold">{providerUserItem.displayName}</h6>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {providerUserItem.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-0">
                    {googleEmailService.isGmailConnected(providerUserItem) ? (
                      <button
                        onClick={() => handleDisconnectGmail(providerUserItem)}
                        className="px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800 transition-colors duration-200 text-sm font-medium flex items-center"
                      >
                        <Icon.Mail className="mr-2 flex-shrink-0" />
                        Disconnect Gmail
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConnectGmail(providerUserItem)}
                        className="px-4 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 transition-colors duration-200 text-sm font-medium flex items-center"
                      >
                        <Icon.Mail className="mr-2 flex-shrink-0" />
                        Connect Gmail
                      </button>
                    )}

                    {googleCalendarService.isCalendarConnected(providerUserItem) ? (
                      <button
                        onClick={() => handleDisconnectCalendar(providerUserItem)}
                        className="px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800 transition-colors duration-200 text-sm font-medium flex items-center"
                      >
                        <Icon.Calendar className="mr-2 flex-shrink-0" />
                        Disconnect Calendar
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConnectCalendar(providerUserItem)}
                        className="px-4 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 transition-colors duration-200 text-sm font-medium flex items-center"
                      >
                        <Icon.Calendar className="mr-2 flex-shrink-0" />
                        Connect Calendar
                      </button>
                    )}

                    {providerUserItem.uid !== appUser.primaryProviderUserId && (
                      <button
                        onClick={() => handleMakePrimary(providerUserItem.uid)}
                        className="px-3 py-1.5 text-sm font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors duration-200"
                      >
                        Make Primary
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast>
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200">
              <HiCheck className="h-5 w-5" />
            </div>
            <div className="ml-3 text-sm font-normal">{successMessage}</div>
            <Toast.Toggle onDismiss={() => setShowSuccessToast(false)} />
          </Toast>
        </div>
      )}

      {/* Error Toast */}
      {showErrorToast && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast>
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-500 dark:bg-red-800 dark:text-red-200">
              <HiX className="h-5 w-5" />
            </div>
            <div className="ml-3 text-sm font-normal">{errorMessage}</div>
            <Toast.Toggle onDismiss={() => setShowErrorToast(false)} />
          </Toast>
        </div>
      )}
    </section>
  );
}
